import { useState, useEffect, useCallback } from 'react';
import { Rental, Bike, Courier } from '@/services/types';
import { toast } from 'sonner';
import { loadRentalsData } from '../RentalDataService';
import { fetchAllRentals, fetchRevenueFromPayments } from '../services/rentalPaginationService';
import { 
  updateRentalStatus, 
  updateBikeStatus,
  cancelSubscriptionsForRental
} from '../services/rentalApi';
import { updateRental } from '../services/rentalMutationApi';
import { generateDealNumber } from '@/utils/dateUtils';

export const useRentalsData = () => {
  const [rentals, setRentals] = useState<Rental[]>([]);
  const [allRentals, setAllRentals] = useState<Rental[]>([]);
  const [bikes, setBikes] = useState<Bike[]>([]);
  const [couriers, setCouriers] = useState<Courier[]>([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [selectedRental, setSelectedRental] = useState<Rental | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<'supabase' | 'mock'>('supabase');
  const [fetchCount, setFetchCount] = useState(0);
  
  // Новое состояние для выручки из платежей
  const [totalRevenue, setTotalRevenue] = useState<number>(0);
  
  // Добавляем отдельные состояния для операций редактирования и блокируем обновления при редактировании
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  // Обновляем функцию для работы с одним источником данных
  const handleRefreshData = () => {
    setFetchCount(prev => prev + 1);
  };

  // Заменяем fetchAllRentalsData на fetchData для загрузки реальных данных из Supabase
  useEffect(() => {
    fetchData();
  }, [fetchCount]);

  // Загружаем выручку из платежей отдельно
  useEffect(() => {
    fetchRevenueData();
  }, [fetchCount]);

  const fetchRevenueData = async () => {
    try {
      const result = await fetchRevenueFromPayments();
      setTotalRevenue(result.totalRevenue);
      
      if (result.connectionError) {
        console.warn('Ошибка при загрузке выручки:', result.connectionError);
      }
    } catch (error: any) {
      console.error('Ошибка при загрузке выручки:', error);
    }
  };

  // Фильтрация данных при изменении фильтра
  const filteredRentals = statusFilter 
    ? rentals.filter(rental => rental.status === statusFilter)
    : rentals;

  // Главная функция для загрузки всех данных (включая реальных курьеров из Supabase)
  const fetchData = useCallback(async () => {
    if (isEditing || isFormOpen) {
      console.log('Пропущено обновление данных, т.к. открыта форма редактирования');
      return;
    }
    
    setLoading(true);
    setStatsLoading(true);
    try {
      const result = await loadRentalsData();
      
      setRentals(result.rentals);
      setAllRentals(result.rentals);
      setBikes(result.bikes);
      setCouriers(result.couriers); // Загружаем реальных курьеров из Supabase
      setDataSource(result.dataSource);
      
      if (result.error) {
        setConnectionError(result.error);
      } else if (result.dataSource === 'mock') {
        setConnectionError('Не удалось подключиться к базе данных. Используются тестовые данные.');
      } else {
        setConnectionError(null);
      }
      
    } catch (error: any) {
      console.error('Ошибка при загрузке данных:', error);
      setConnectionError(error.message || 'Неизвестная ошибка при загрузке данных');
    } finally {
      setLoading(false);
      setStatsLoading(false);
    }
  }, [isEditing, isFormOpen]);

  // Обработчик добавления новой аренды - ТОЛЬКО ВЕБХУК, НИКАКИХ ЗАПИСЕЙ В БАЗУ
  const handleAddRental = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    
    const form = event.currentTarget;
    const formData = new FormData(form);
    
    const courierId = formData.get('courier') as string;
    const startDateStr = formData.get('startDate') as string;
    const cost = parseFloat(formData.get('cost') as string) || 500;
    const tariffKey = formData.get('tariffKey') as string;
    const tariffTitle = formData.get('tariffTitle') as string;
    const isVip = formData.get('isVip') === 'yes';
    
    if (!courierId || !startDateStr) {
      toast("Пожалуйста, заполните все поля");
      setLoading(false);
      return;
    }
    
    try {
      const courier = couriers.find(c => c.id === courierId);
      
      if (!courier) {
        throw new Error('Курьер не найден');
      }
      
      try {
        const webhookUrl = 'https://hook.eu2.make.com/ibit73nie2ftbuw4n7vnt55f1xoc17mp';
        
        const tariffEnumId = {
          'week': 'weekly',
          '2weeks': 'biweekly',
          'month': 'monthly',
          'buyout': 'rent-to-own'
        }[tariffKey] || tariffKey;
        
        const dealNumber = generateDealNumber(isVip);
        
        const payload = {
          data: {
            phone: courier.phone || null,
            tariff: tariffTitle,
            price: cost,
            dealNumber: dealNumber,
            tariffType: tariffEnumId,
            isVip: isVip
          }
        };
        
        console.log('Отправка данных на webhook:', JSON.stringify(payload));
        
        await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        
        toast("Заявка отправлена!");
        setShowAddDialog(false);
        
      } catch (webhookError) {
        console.error('Ошибка при отправке вебхука:', webhookError);
        throw new Error('Ошибка при отправке заявки');
      }
      
    } catch (error: any) {
      console.error('Ошибка при оформлении заявки:', error);
      toast.error(`Ошибка: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Обработчик изменения статуса аренды
  const handleChangeStatus = async (status: Rental['status']) => {
    if (!selectedRental) return;
    setLoading(true);
    
    try {
      let updateData: any = {
        status: status
      };
      
      if (status === 'completed' || status === 'cancelled') {
        updateData.end_date = new Date().toISOString();
      }
      
      const rentalResult = await updateRentalStatus(selectedRental.id, updateData);
      
      if (!rentalResult.success) {
        throw new Error(`Ошибка при обновлении статуса: ${JSON.stringify(rentalResult.error)}`);
      }
      
      if ((status === 'completed' || status === 'cancelled') && selectedRental.bikeId) {
        await updateBikeStatus(selectedRental.bikeId, 'available');
      }
      
      if (status === 'active' && selectedRental.bikeId) {
        await updateBikeStatus(selectedRental.bikeId, 'in_use');
      }
      
      if (status === 'completed' || status === 'cancelled') {
        const cancelResult = await cancelSubscriptionsForRental(selectedRental.id);
        if (cancelResult.success) {
          console.log('Результат отмены подписок:', cancelResult.message);
          if (cancelResult.message.includes('Отменено')) {
            toast(`Связанные подписки автоматически отменены: ${cancelResult.message}`);
          }
        } else {
          console.error('Ошибка при отмене подписок:', cancelResult.message);
          toast.warning(`Внимание: возникла проблема с отменой подписок: ${cancelResult.message}`);
        }
      }
      
      toast(`Статус аренды изменен на "${status === 'active' ? 'Активна' : status === 'completed' ? 'Завершена' : 'Отменена'}"`);
      
      setAllRentals(prev => 
        prev.map(r => 
          r.id === selectedRental.id ? { 
            ...r, 
            status, 
            endDate: (status === 'completed' || status === 'cancelled') ? new Date().toISOString() : r.endDate 
          } : r
        )
      );
      
      setShowDetailsDialog(false);
      handleRefreshData();
    } catch (error: any) {
      console.error('Ошибка при изменении статуса аренды:', error);
      toast.error(`Ошибка при изменении статуса: ${error.message || 'Неизвестная ошибка'}`);
    } finally {
      setLoading(false);
    }
  };

  // Обработчик редактирования аренды
  const handleEditRental = async (rentalData: any) => {
    if (!selectedRental) return false;
    
    setIsEditing(true);
    
    try {
      const apiData = {
        bike_id: rentalData.bike_id,
        courier_id: rentalData.courier_id,
        amount: rentalData.amount,
        status: rentalData.status,
        start_date: rentalData.start_date,
        end_date: rentalData.end_date
      };

      const oldBikeId = selectedRental.bikeId;
      const newBikeId = apiData.bike_id;
      const wasStatusChanged = selectedRental.status !== apiData.status;

      console.log('Отправка данных для обновления аренды:', apiData);

      const result = await updateRental(selectedRental.id, apiData);

      if (result.error) {
        throw new Error(`Ошибка при обновлении: ${JSON.stringify(result.error)}`);
      }

      if (oldBikeId !== newBikeId) {
        await updateBikeStatus(oldBikeId, 'available');
        
        if (apiData.status === 'active') {
          await updateBikeStatus(newBikeId, 'in_use');
        }
      } else if (wasStatusChanged) {
        if (apiData.status === 'active') {
          await updateBikeStatus(newBikeId, 'in_use');
        } else if (apiData.status === 'completed' || apiData.status === 'cancelled') {
          await updateBikeStatus(newBikeId, 'available');
        }
      }

      toast.success("Данные аренды успешно обновлены");

      if (selectedRental) {
        setSelectedRental({
          ...selectedRental,
          bike_id: apiData.bike_id,
          bikeId: apiData.bike_id,
          courier_id: apiData.courier_id,
          courierId: apiData.courier_id,
          cost: apiData.amount,
          amount: apiData.amount,
          status: apiData.status,
          start_date: apiData.start_date,
          startDate: apiData.start_date,
          end_date: apiData.end_date,
          endDate: apiData.end_date
        });
      }

      setTimeout(() => {
        setIsFormOpen(false);
        setIsEditing(false);
        handleRefreshData();
      }, 500);

      return true;
    } catch (error: any) {
      console.error('Ошибка при редактировании аренды:', error);
      toast.error(`Ошибка при редактировании: ${error.message || 'Неизвестная ошибка'}`);
      setIsEditing(false);
      return false;
    }
  };

  return {
    rentals: filteredRentals,
    allRentals,
    bikes,
    couriers,
    loading,
    statsLoading,
    selectedRental,
    showAddDialog,
    showDetailsDialog,
    connectionError,
    dataSource,
    isEditing,
    isFormOpen,
    totalCount: allRentals.length,
    totalRevenue,
    setSelectedRental,
    setShowAddDialog,
    setShowDetailsDialog,
    setStatusFilter,
    setIsFormOpen,
    fetchData,
    handleAddRental,
    handleChangeStatus,
    handleEditRental,
    handleRefreshData
  };
};
